import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { StatusPill } from '../../components/StatusPills';

const PROCESSES = [
  { id: '', label: 'All processes' },
  { id: 'segmentation', label: 'Segmentation' },
  { id: 'record_matching', label: 'Record matching' },
  { id: 'text_extraction', label: 'Text extraction' },
  { id: 'email_classification', label: 'Email classification' },
  { id: 'email_response_generation', label: 'Email response generation' },
  { id: 'vision_ocr', label: 'Vision OCR' },
  { id: 'follow_up_scope_cleanup', label: 'Follow-up wording cleanup' },
  { id: 'file_review', label: 'Closer read of a file' },
  { id: 'org_summary', label: 'Summary of time with an organisation' },
];

const AdminAnalysisFlagged = () => {
  const [process, setProcess] = useState('');
  const [data, setData] = useState({ results: [], note: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .flaggedRuns(process || undefined)
      .then((res) => !cancelled && setData(res.data || { results: [] }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [process]);

  return (
    <div className="space-y-6" data-testid="admin-analysis-flagged">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground">Flagged runs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Process runs the AI flagged as low-confidence, errored, or fell back to
            the system provider. Use this to compare model performance and catch failing keys.
          </p>
        </div>
        <div className="w-56">
          <Select value={process || 'all'} onValueChange={(v) => setProcess(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="flagged-process-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROCESSES.map((p) => (
                <SelectItem key={p.id || 'all'} value={p.id || 'all'}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : data.results?.length ? (
        <div className="space-y-3">
          {data.results.map((r) => {
            const isFallback = r.event_type === 'fallback_to_system_provider';
            return (
              <Card key={r.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <StatusPill tone="needs" size="sm" className="mt-0.5">Flagged</StatusPill>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">
                        {r.process_id}
                        {r.document_id && <span className="text-muted-foreground"> · doc {r.document_id}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {r.flag_reason || 'Flagged'}
                      </p>
                    </div>
                    {r.created_at && (
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {(r.ai_provider_name || r.ai_model_used || isFallback) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.ai_provider_name && (
                        <Badge variant="secondary" className="text-[10px]">
                          {r.ai_provider_name} ({r.ai_provider_type})
                        </Badge>
                      )}
                      {r.ai_model_used && (
                        <Badge variant="secondary" className="text-[10px] font-mono">
                          {r.ai_model_used}
                        </Badge>
                      )}
                      {r.ai_model_requested && r.ai_model_used !== r.ai_model_requested && (
                        <span className="text-[10px] text-muted-foreground">
                          (requested {r.ai_model_requested})
                        </span>
                      )}
                      {isFallback && r.primary_provider_name && (
                        <StatusPill tone="breach" size="sm">
                          Fell back from {r.primary_provider_name} / {r.primary_model}
                        </StatusPill>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground space-y-2">
            <p>{data.note || 'Nothing flagged for review.'}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAnalysisFlagged;
